/* Doorbell ringing tone — long telephone-style ring synthesized via
   Web Audio (no audio asset needed). Classic dual-tone (440 + 480 Hz)
   "ring-ring … ring-ring" cadence that loops for the whole ringing
   stage, like WhatsApp/phone calls, instead of a short chime. */
import { useEffect, useRef } from "react";

export default function useDoorbellTone(playing) {
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!playing) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      if (ctxRef.current) {
        // ctx.close() returns a Promise — wrap rejection (which can fire
        // when the context is already closed from a previous toggle) so
        // it doesn't escape as an unhandled-promise-rejection.
        ctxRef.current.close().catch(() => { /* already closed */ });
        ctxRef.current = null;
      }
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    ctxRef.current = ctx;

    // One "burst" = both telephone frequencies together with a soft
    // attack/release envelope.
    const burst = (start, dur) => {
      [440, 480].forEach((f) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(f, start);
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.22, start + 0.03);
        gain.gain.setValueAtTime(0.22, start + dur - 0.06);
        gain.gain.linearRampToValueAtTime(0.0001, start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + dur);
      });
    };

    // "Ring-ring" (UK-style double burst), then a breath, on a 3.5 s
    // cycle — reads unmistakably as an incoming call and keeps ringing
    // for the whole 60 s window.
    const playRing = () => {
      const now = ctx.currentTime;
      burst(now, 0.8);
      burst(now + 1.0, 0.8);
    };
    playRing();
    intervalRef.current = setInterval(playRing, 3500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // ctx.close() is a Promise — silence rejection if it's already
      // been closed by the !playing branch above (double-close on a
      // fast stage transition is a known race).
      ctx.close().catch(() => { /* already closed */ });
    };
  }, [playing]);
}
